import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import StatusBadge from "../StatusBadge";
import * as permissions from "../../pages/app_props";
const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [5, 10, 20, 25, 50, 100];
const SESSION_STORAGE_PAGE_SIZE_KEY = "compliance_ui_page_size";

export default function DataTable({
  schema,
  columns: customColumns,
  rows = [],
  onEdit,
  onDelete,
  extraControls = null,
  onCreateTask,
  onFiltersChange,
  onPageSizeChange,
  totalCount = null,
  currentPage = undefined,
  onPageChange,
  onReview,
}) {
  const [page, setPage] = useState(currentPage || 0);
  const user = JSON.parse(sessionStorage.getItem("userInfo"));
  const [pageSize, setPageSize] = useState(() => {
    try {
      const stored = window.sessionStorage.getItem(
        SESSION_STORAGE_PAGE_SIZE_KEY,
      );
      const n = Number(stored);
      if (n && PAGE_SIZE_OPTIONS.includes(n)) return n;
    } catch (e) {
      // ignore
    }
    return DEFAULT_PAGE_SIZE;
  });
  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [activeRow, setActiveRow] = useState(null);
  const [columnFilters, setColumnFilters] = useState({});

  React.useEffect(() => {
    // debug: log when rows prop changes and internal paging state
    // eslint-disable-next-line no-console
    // console.log(
    //   "DataTable: received rows",
    //   rows.length,
    //   "currentPage prop:",
    //   currentPage,
    //   "internal page:",
    //   page,
    //   "pageSize:",
    //   pageSize,
    //   "totalCount:",
    //   totalCount,
    // );
  }, [rows, currentPage, page, pageSize, totalCount]);

  const columns = useMemo(() => {
    if (customColumns && customColumns.length) {
      return customColumns.map((column) => ({
        key: column.id || column.accessorKey,
        header: column.header || column.accessorKey,
        field: column,
      }));
    }

    const src =
      schema.listColumns && schema.listColumns.length
        ? schema.listColumns
        : schema.fields.slice(0, 5);
    return src.map((field) => {
      // ensure we use the full field definition from schema.fields when available
      const fullField = (schema.fields || []).find(
        (f) => f.name === field.name,
      );
      const useField = fullField ? { ...fullField, ...field } : field;
      return {
        key: field.name,
        header: field.label || field.name,
        field: useField,
      };
    });
  }, [customColumns, schema]);
  const filteredRows = useMemo(() => {
    if (onFiltersChange) {
      return rows;
    }

    return rows.filter((row) =>
      columns.every(({ key, field }) => {
        const filterValue = (columnFilters[key] || "").trim().toLowerCase();

        if (!filterValue || filterValue.length < 3) {
          return true;
        }

        const raw = getFieldValueForRow(row, field);
        const text = stringifyValue(raw).toLowerCase();
        return text.includes(filterValue);
      }),
    );
  }, [columnFilters, columns, rows]);

  React.useEffect(() => {
    if (!onFiltersChange) {
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      const activeFilters = Object.entries(columnFilters).reduce(
        (accumulator, [key, value]) => {
          const normalized = (value || "").trim();
          if (normalized.length >= 3) {
            accumulator[key] = normalized;
          }
          return accumulator;
        },
        {},
      );

      onFiltersChange(activeFilters);
    }, 300);

    return () => window.clearTimeout(timerId);
  }, [columnFilters, onFiltersChange]);

  // const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const effectiveTotal =
    typeof totalCount === "number" ? totalCount : filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(effectiveTotal / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  // If totalCount is provided we assume server-side pagination and that
  // `rows` already contains the current page's content; avoid slicing
  // the array again which would produce empty results.
  const pagedRows =
    typeof totalCount === "number"
      ? filteredRows
      : filteredRows.slice(safePage * pageSize, safePage * pageSize + pageSize);

  // sync external currentPage -> internal
  React.useEffect(() => {
    if (typeof currentPage === "number" && currentPage !== page) {
      setPage(currentPage);
    }
  }, [currentPage]);

  // notify parent when internal page changes
  React.useEffect(() => {
    if (typeof onPageChange === "function") {
      try {
        onPageChange(page);
      } catch (e) {
        // ignore
      }
    }
  }, [page, onPageChange]);

  function openRowActions(event, row, index) {
    setActionAnchorEl(event.currentTarget);
    setActiveRow({ row, index });
  }

  function closeRowActions() {
    setActionAnchorEl(null);
    setActiveRow(null);
  }

  function handleEdit() {
    if (activeRow && onEdit) {
      onEdit(activeRow.index, activeRow.row);
    }
    closeRowActions();
  }

  function handleDelete() {
    if (activeRow && onDelete) {
      onDelete(activeRow.index, activeRow.row);
    }
    closeRowActions();
  }

  function handleCreateTask() {
    if (activeRow && onCreateTask) {
      onCreateTask(activeRow.index, activeRow.row);
    }
    closeRowActions();
  }

  function handleReview() {
    if (activeRow && typeof onReview === "function") {
      onReview(activeRow.index, activeRow.row);
    }
    closeRowActions();
  }

  // determine module-specific permission names
  function resolveModulePermissions(moduleName) {
    if (!moduleName) return {};
    const m = String(moduleName).toLowerCase();
    switch (m) {
      case "regulations":
      case "regulation":
        return {
          add: permissions.COMPLIANCE_CAN_ADD_REGULATIONS,
          del: permissions.COMPLIANCE_CAN_DELETE_REGULATIONS,
        };
      case "standards":
      case "standard":
        return {
          add: permissions.COMPLIANCE_CAN_ADD_STANDARDS,
          del: permissions.COMPLIANCE_CAN_DELETE_STANDARDS,
        };
      case "requirements":
      case "requirement":
        return {
          add: permissions.COMPLIANCE_CAN_ADD_REQUIREMENTS,
          del: permissions.COMPLIANCE_CAN_DELETE_REQUIREMENTS,
        };
      case "tasks":
      case "task":
        return {
          add: permissions.COMPLIANCE_CAN_ADD_TASKS,
          del: permissions.COMPLIANCE_CAN_DELETE_TASKS,
        };
      case "masterdatacompliance":
      case "masterdatacompliancetypes":
      case "masterdatalocations":
      case "masterdatatasktemplates":
        return {
          add: permissions.COMPLIANCE_CAN_MANAGE_MASTERDATA,
          del: permissions.COMPLIANCE_CAN_MANAGE_MASTERDATA,
        };
      default:
        return {};
    }
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "center",
          justifyContent: "flex-end",
          mb: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ marginLeft: "auto" }}>{extraControls}</Box>
      </Box>

      <Box
        sx={{
          backgroundColor: "#fff",
          borderRadius: "16px",
          border: "1px solid #e5e7eb",
          overflow: "hidden",
        }}
      >
        <Box sx={{ overflowX: "auto", maxHeight: "65vh", overflowY: "auto" }}>
          <Box
            component="table"
            sx={{
              width: "100%",
              borderCollapse: "separate",
              borderSpacing: 0,
              minWidth: 900,
            }}
          >
            <Box component="thead" sx={{ backgroundColor: "#f8fafc" }}>
              <Box component="tr">
                {columns.map((column) => (
                  <Box component="th" key={column.key} sx={headerCellStyles}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Box component="span">{column.header}</Box>
                      <TextField
                        size="small"
                        placeholder={`Search ${column.header}`}
                        value={columnFilters[column.key] || ""}
                        onChange={(event) => {
                          setColumnFilters((current) => ({
                            ...current,
                            [column.key]: event.target.value,
                          }));
                          setPage(0);
                        }}
                        sx={{
                          width: "100%",
                          minWidth: 140,
                          backgroundColor: "#fff",
                          "& .MuiOutlinedInput-root": {
                            fontSize: "0.82rem",
                            backgroundColor: "#fff",
                          },
                          "& .MuiInputBase-input": {
                            py: 1,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                ))}
                <Box
                  component="th"
                  sx={{
                    ...headerCellStyles,
                    width: 72,
                    textAlign: "center",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "center",
                      minHeight: 68,
                    }}
                  >
                    Action
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box component="tbody">
              {pagedRows.length ? (
                pagedRows.map((row, pageIndex) => {
                  const absoluteIndex = safePage * pageSize + pageIndex;

                  return (
                    <Box
                      component="tr"
                      key={getRowKey(row, absoluteIndex)}
                      sx={{
                        "&:hover": {
                          backgroundColor: "#f8fbff",
                        },
                      }}
                    >
                      {columns.map((column) => (
                        <Box
                          component="td"
                          key={column.key}
                          sx={bodyCellStyles}
                        >
                          {renderCellValue(
                            getFieldValueForRow(row, column.field),
                          )}
                        </Box>
                      ))}
                      {/* Only show action controls when task is pending */}
                      <Box
                        component="td"
                        sx={{
                          ...bodyCellStyles,
                          width: 72,
                          textAlign: "center",
                        }}
                      >
                        {(!row._statusRaw ||
                          row._statusRaw === "PENDING" ||
                          !row.status ||
                          row.status === "Pending") && (
                          <>
                            <IconButton
                              onClick={(event) =>
                                openRowActions(event, row, absoluteIndex)
                              }
                              size="small"
                              aria-label="Row actions"
                              sx={{
                                color: "#111827",
                              }}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    </Box>
                  );
                })
              ) : (
                <Box component="tr">
                  <Box
                    component="td"
                    colSpan={columns.length + 1}
                    sx={{
                      px: 3,
                      py: 5,
                      textAlign: "center",
                      color: "#6b7280",
                      fontSize: "0.95rem",
                      borderTop: "1px solid #e5e7eb",
                    }}
                  >
                    No records found.
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            px: 3,
            py: 2,
            borderTop: "1px solid #e5e7eb",
            backgroundColor: "#fcfcfd",
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="dt-page-size-label">Page size</InputLabel>
              <Select
                labelId="dt-page-size-label"
                value={pageSize}
                label="Page size"
                onChange={(e) => {
                  const newSize = Number(e.target.value);
                  setPageSize(newSize);
                  try {
                    sessionStorage.setItem(
                      SESSION_STORAGE_PAGE_SIZE_KEY,
                      String(newSize),
                    );
                  } catch (err) {
                    // ignore
                  }
                  setPage(0);
                  if (typeof onPageSizeChange === "function") {
                    try {
                      onPageSizeChange(newSize);
                    } catch (err) {
                      // ignore
                    }
                  }
                }}
              >
                {PAGE_SIZE_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Typography
              variant="body2"
              sx={{ color: "#4b5563", display: "flex", alignItems: "center" }}
            >
              Showing {effectiveTotal ? safePage * pageSize + 1 : 0}-
              {Math.min((safePage + 1) * pageSize, effectiveTotal)} of{" "}
              {effectiveTotal}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              disabled={safePage === 0}
              onClick={() => setPage((current) => Math.max(0, current - 1))}
              sx={{ borderRadius: "10px", textTransform: "none" }}
            >
              Previous
            </Button>
            <Button
              variant="outlined"
              size="small"
              disabled={safePage >= totalPages - 1}
              onClick={() =>
                setPage((current) => Math.min(totalPages - 1, current + 1))
              }
              sx={{ borderRadius: "10px", textTransform: "none" }}
            >
              Next
            </Button>
          </Box>
        </Box>
      </Box>

      <Menu
        anchorEl={actionAnchorEl}
        open={Boolean(actionAnchorEl)}
        onClose={closeRowActions}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            minWidth: 180,
            mt: 0.5,
            borderRadius: 2,
            boxShadow: "0 12px 28px rgba(15, 23, 42, 0.18)",
          },
        }}
      >
        {onReview ? (
          <MenuItem onClick={handleReview}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Review</ListItemText>
          </MenuItem>
        ) : null}
        {(() => {
          const modulePerms = resolveModulePermissions(schema && schema.module);
          const canEdit =
            onEdit &&
            user &&
            modulePerms.add &&
            Array.isArray(user.authorities) &&
            user.authorities.includes(modulePerms.add);

          const canDelete =
            onDelete &&
            user &&
            modulePerms.del &&
            Array.isArray(user.authorities) &&
            user.authorities.includes(modulePerms.del);

          return (
            <>
              {canEdit ? (
                <MenuItem onClick={handleEdit}>
                  <ListItemIcon>
                    <EditIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Edit</ListItemText>
                </MenuItem>
              ) : null}
              {canDelete ? (
                <MenuItem onClick={handleDelete}>
                  <ListItemIcon>
                    <DeleteIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Delete</ListItemText>
                </MenuItem>
              ) : null}
            </>
          );
        })()}
        {onCreateTask ? (
          <MenuItem onClick={handleCreateTask}>
            <ListItemIcon>
              <AssignmentTurnedInIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Create Task</ListItemText>
          </MenuItem>
        ) : null}
      </Menu>
    </Box>
  );
}

const headerCellStyles = {
  px: 3,
  py: 2.25,
  textAlign: "left",
  fontSize: "0.83rem",
  fontWeight: 700,
  color: "#111827",
  borderBottom: "1px solid #dbe1ea",
  whiteSpace: "nowrap",
  position: "sticky",
  top: 0,
  backgroundColor: "#f8fafc",
  zIndex: 2,
};

const bodyCellStyles = {
  px: 3,
  py: 2,
  fontSize: "0.94rem",
  color: "#111827",
  verticalAlign: "top",
  borderTop: "1px solid #edf1f5",
};

function renderCellValue(value) {
  if (typeof value === "string" && value.includes("\n")) {
    return (
      <Box>
        {value.split("\n").map((line) => (
          <Typography
            key={line}
            component="div"
            sx={{
              fontSize: "0.94rem",
              lineHeight: 1.45,
              color: line.includes(":") ? "#4b5563" : "#111827",
            }}
          >
            {line}
          </Typography>
        ))}
      </Box>
    );
  }

  if (typeof value === "string" && value.length > 80) {
    return (
      <Typography sx={{ fontSize: "0.94rem", lineHeight: 1.45 }}>
        {value}
      </Typography>
    );
  }

  return value;
}

function stringifyValue(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (React.isValidElement(value)) {
    return "";
  }
  if (Array.isArray(value)) {
    return value.map(stringifyValue).join(" ");
  }
  if (typeof value === "object") {
    if (value.name) return value.name;
    return Object.values(value).map(stringifyValue).join(" ");
  }
  return String(value);
}

function getRowKey(row, index) {
  return row?.id || row?._id || row?.recordNo || row?.title || index;
}

function getFieldValueForRow(row, field) {
  if (!field) return "";

  if (field.accessorFn) {
    return field.accessorFn(row);
  }

  if (field.Cell) {
    return field.Cell({
      row: { original: row },
      cell: {
        getValue: () =>
          field.accessorKey ? row[field.accessorKey] : undefined,
      },
    });
  }

  if (field.type !== "group" && field.type !== "list") {
    if (field.name === "summary") return row.title || row.summary || "";
    if (field.name === "source") {
      const sc = row.sourceComplianceRegulationStandard;
      if (sc && typeof sc === "object") {
        return sc.title || sc.regulationName || sc.name || "";
      }
      return row.source || "";
    }
    if (field.name === "status") {
      return <StatusBadge status={row[field.name]} />;
    }
    return formatRowValue(row[field.name], field);
  }

  if (
    field.type === "group" &&
    Array.isArray(field.fields) &&
    field.fields.length
  ) {
    const statusSub = field.fields.find(
      (subField) => subField.name === "status",
    );
    const primarySub = statusSub || field.fields[0];
    const nestedObj = row[field.name];

    if (
      nestedObj &&
      typeof nestedObj === "object" &&
      primarySub.name in nestedObj
    ) {
      return primarySub.name === "status" ? (
        <StatusBadge status={nestedObj[primarySub.name]} />
      ) : (
        formatRowValue(nestedObj[primarySub.name], primarySub)
      );
    }

    if (primarySub.name in row) {
      return primarySub.name === "status" ? (
        <StatusBadge status={row[primarySub.name]} />
      ) : (
        formatRowValue(row[primarySub.name], primarySub)
      );
    }

    return "";
  }

  if (field.type === "list") {
    const arr = row[field.name];
    if (Array.isArray(arr)) return arr.length;
    return "";
  }

  return "";
}

function formatRowValue(value, field) {
  if (value === null || value === undefined || value === "") return "";
  // Apply valueMap from schema field if present
  if (field && field.valueMap) {
    try {
      const key = typeof value === "boolean" ? String(value) : String(value);
      if (key in field.valueMap) return field.valueMap[key];
    } catch (e) {
      // ignore and fall back
    }
  }
  if (typeof value === "object") {
    if (value.name) return value.name;
    if (value.location) return value.location;
    return JSON.stringify(value);
  }

  if (field && field.name === "location" && typeof value === "string") {
    return value;
  }

  return value;
}
